import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { Check, Server, HardDrive, ArrowRight, Users } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [processingPlan, setProcessingPlan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/plans`);
      setPlans(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des tarifs");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    if (!isAuthenticated) {
      navigate("/register");
      return;
    }
    setProcessingPlan(plan.id);
    try {
      const res = await axios.post(
        `${API_URL}/api/checkout/session`,
        { plan_id: plan.id, billing_cycle: billingCycle, origin_url: window.location.origin },
        { headers: getAuthHeader() }
      );
      window.location.href = res.data.url;
    } catch (error) {
      toast.error("Erreur lors de la creation de la session de paiement");
      setProcessingPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#07090E] flex items-center justify-center">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07090E]">
      <PublicNavbar />

      <section className="pt-32 pb-8 sm:pt-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-4">Tarifs</p>
          <h1
            className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Des tarifs simples et transparents
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
            14 jours d'essai gratuit, sans engagement, sans carte bancaire.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-[#0D111A] border border-slate-800/60" data-testid="billing-toggle">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                billingCycle === "monthly"
                  ? "bg-cyan-500 text-slate-950"
                  : "text-slate-400 hover:text-white"
              }`}
              data-testid="billing-monthly-btn"
            >
              Mensuel
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === "yearly"
                  ? "bg-cyan-500 text-slate-950"
                  : "text-slate-400 hover:text-white"
              }`}
              data-testid="billing-yearly-btn"
            >
              Annuel
              <span className="text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-semibold">-17%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-12" data-testid="pricing-plans">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, index) => {
              const isPro = index === 1;
              return (
                <div
                  key={plan.id}
                  className={`relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1 ${
                    isPro
                      ? "border-2 border-cyan-500/50 bg-[#0D111A] shadow-xl shadow-cyan-500/10"
                      : "border border-slate-800/60 bg-[#0D111A]/70"
                  }`}
                  data-testid={`pricing-card-${index}`}
                >
                  {isPro && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap">
                      <span className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-slate-950 text-xs font-semibold px-4 py-1.5 rounded-full">
                        Populaire pour les MSPs
                      </span>
                    </div>
                  )}

                  <h3 className="text-xl font-semibold text-white mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {plan.name}
                  </h3>
                  <p className="text-slate-500 text-sm mb-6">{plan.description}</p>

                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {billingCycle === "monthly" ? plan.price_monthly : plan.price_yearly}€
                    </span>
                    <span className="text-slate-500 text-sm ml-1">
                      /{billingCycle === "monthly" ? "mois" : "an"}
                    </span>
                  </div>

                  <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-3 text-slate-300 text-sm">
                      <Server size={16} className="text-cyan-400 flex-shrink-0" />
                      <span>{plan.max_servers === 999 ? "Illimite" : plan.max_servers} serveurs</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-300 text-sm">
                      <Users size={16} className="text-cyan-400 flex-shrink-0" />
                      <span>{plan.max_clients === 999 ? "Illimite" : (plan.max_clients || 50)} clients</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-300 text-sm">
                      <HardDrive size={16} className="text-cyan-400 flex-shrink-0" />
                      <span>{plan.max_agents === 99 ? "Illimite" : plan.max_agents} types d'agents</span>
                    </div>
                    {plan.features?.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                        <Check size={16} className="text-emerald-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={processingPlan === plan.id}
                    className={`w-full py-5 rounded-xl font-semibold transition-all ${
                      isPro
                        ? "bg-cyan-500 hover:bg-cyan-400 text-slate-950 shadow-lg shadow-cyan-500/20"
                        : "bg-slate-800 hover:bg-slate-700 text-white"
                    }`}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    {processingPlan === plan.id ? "Chargement..." : "Commencer l'essai gratuit"}
                    {processingPlan !== plan.id && <ArrowRight size={16} className="ml-2" />}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#0D111A]/30" data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold text-white text-center mb-10" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Questions frequentes
          </h2>
          <div className="space-y-4">
            {[
              { q: "L'essai gratuit est-il sans engagement ?", a: "Oui, vous beneficiez de 14 jours d'essai gratuit sans carte bancaire. A la fin de l'essai, vous choisissez de vous abonner ou votre compte passe en lecture seule." },
              { q: "Puis-je changer de formule ?", a: "Oui, vous pouvez passer a une formule superieure a tout moment. Le changement est effectif immediatement et le prorata est calcule automatiquement." },
              { q: "Quels logiciels de sauvegarde sont supportes ?", a: "Actuellement, ServeurOnline supporte Veeam Backup & Replication et Iperius Backup. D'autres agents sont en cours de developpement." },
              { q: "Comment fonctionne le deploiement des agents ?", a: "Un simple script PowerShell est a executer sur vos serveurs Windows. L'agent remonte automatiquement les informations de sauvegarde. Aucune installation complexe requise." },
              { q: "Mes donnees sont-elles securisees ?", a: "Oui, les donnees sont chiffrees, hebergees en Europe, et chaque compte utilisateur est completement isole (architecture multi-tenant)." },
            ].map((item, i) => (
              <details key={i} className="group p-5 rounded-xl border border-slate-800/60 bg-[#0D111A]/70">
                <summary className="flex items-center justify-between cursor-pointer text-white font-medium text-sm list-none">
                  {item.q}
                  <span className="text-slate-500 group-open:rotate-45 transition-transform text-lg">+</span>
                </summary>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
